import type { Flashcard, KnownScoreRule } from '@/types/models';

const MATRIX_GRID_TYPE = 'Matrix Grid';
const PLUS_MINUS_RULE: KnownScoreRule = '+/-';
const ZERO_ONE_RULE: KnownScoreRule = '0/1';
const DYAD_RULE: KnownScoreRule = 'dyad';
const TRIAD_RULE: KnownScoreRule = 'triad';
const KNOWN_SCORE_RULES: readonly KnownScoreRule[] = [
  PLUS_MINUS_RULE,
  ZERO_ONE_RULE,
  DYAD_RULE,
  TRIAD_RULE,
];

function isKnownScoreRule(rule: string | null): rule is KnownScoreRule {
  return rule !== null && (KNOWN_SCORE_RULES as readonly string[]).includes(rule);
}

export function scoreRuleLabel(rule: string | null): string {
  if (rule === DYAD_RULE) return 'Rationale (Dyad)';
  if (rule === TRIAD_RULE) return 'Rationale (Triad)';
  return rule ?? '';
}

interface MatrixGridSelection {
  answerId: number;
  selections: boolean[];
}

export type RawAnswerData = Record<string, unknown> | null | undefined;

function getMatrixGridSelections(rawAnswerData: RawAnswerData): MatrixGridSelection[] {
  const selections = rawAnswerData?.matrixGridSelections;
  return Array.isArray(selections) ? selections as MatrixGridSelection[] : [];
}

export function useNextGenScoring() {
  function maxScore(flashcard: Flashcard): number {
    const scoreRule = nextGenScoringRule(flashcard);
    if (scoreRule === DYAD_RULE) return 1;
    if (scoreRule === TRIAD_RULE) return maxScoreTriad(flashcard);
    if (flashcard.type === MATRIX_GRID_TYPE) return maxScoreMatrixGrid(flashcard);
    return flashcard.answers.filter((answer) => answer.correct).length;
  }

  function nextGenScoringRule(flashcard: Flashcard): KnownScoreRule | null {
    return isKnownScoreRule(flashcard.scoreRule) ? flashcard.scoreRule : null;
  }

  function maxScoreMatrixGrid(flashcard: Flashcard): number {
    return flashcard.answers.reduce((acc, answer) => {
      return acc + answer.matrixGridCorrect.filter((a) => a).length;
    }, 0);
  }

  function nextGenScoreMatrixGrid(
    flashcard: Flashcard,
    rawAnswerData: RawAnswerData,
    scoreRule: KnownScoreRule | null,
  ): number {
    const selections = getMatrixGridSelections(rawAnswerData);
    return flashcard.answers.reduce((total, answer) => {
      const userAnswer = selections.find((s) => s.answerId === answer.id);
      if (!userAnswer) return total;
      const rowScore = answer.matrixGridCorrect.reduce<number>((rowAcc, isCorrect, idx) => {
        if (!userAnswer.selections[idx]) return rowAcc;
        if (isCorrect) return rowAcc + 1;
        if (scoreRule === PLUS_MINUS_RULE) return rowAcc - 1;
        return rowAcc;
      }, 0);
      return total + Math.max(0, rowScore);
    }, 0);
  }

  function dropdownIdentifiers(flashcard: Flashcard): string[] {
    return [...new Set(
      flashcard.answers
        .map((answer) => answer.dropDownIdentifier)
        .filter((identifier) => identifier !== null)
        .map(String),
    )].sort((a, b) => {
      const numericDifference = Number(a) - Number(b);
      return Number.isNaN(numericDifference) ? a.localeCompare(b) : numericDifference;
    });
  }

  function selectedDropdownAnswers(flashcard: Flashcard, answerIds: number[]): Map<string, Flashcard['answers'][number]> {
    return flashcard.answers.reduce((selected, answer) => {
      if (answer.dropDownIdentifier !== null && answerIds.includes(answer.id)) {
        selected.set(String(answer.dropDownIdentifier), answer);
      }
      return selected;
    }, new Map<string, Flashcard['answers'][number]>());
  }

  function maxScoreTriad(flashcard: Flashcard): number {
    return Math.max(1, dropdownIdentifiers(flashcard).length - 1);
  }

  function nextGenScoreDyad(flashcard: Flashcard, answerIds: number[]): number {
    const identifiers = dropdownIdentifiers(flashcard);
    if (identifiers.length === 0) return 0;

    const selected = selectedDropdownAnswers(flashcard, answerIds);
    return identifiers.every((identifier) => selected.get(identifier)?.correct) ? 1 : 0;
  }

  function nextGenScoreTriad(flashcard: Flashcard, answerIds: number[]): number {
    const [claim, ...reasons] = dropdownIdentifiers(flashcard);
    if (claim === undefined) return 0;

    const selected = selectedDropdownAnswers(flashcard, answerIds);
    if (!selected.get(claim)?.correct) return 0;
    return reasons.filter((identifier) => selected.get(identifier)?.correct).length;
  }

  function nextGenScore(flashcard: Flashcard, answerIds: number[], rawAnswerData?: RawAnswerData): number {
    const scoreRule = nextGenScoringRule(flashcard);
    if (flashcard.type === MATRIX_GRID_TYPE) {
      return nextGenScoreMatrixGrid(flashcard, rawAnswerData, scoreRule);
    }
    if (scoreRule === DYAD_RULE) return nextGenScoreDyad(flashcard, answerIds);
    if (scoreRule === TRIAD_RULE) return nextGenScoreTriad(flashcard, answerIds);
    const selected = flashcard.answers.filter((answer) => answerIds.includes(answer.id));
    const correctSelected = selected.filter((answer) => answer.correct).length;
    if (scoreRule === PLUS_MINUS_RULE) {
      return Math.max(0, correctSelected - (selected.length - correctSelected));
    }
    return correctSelected;
  }

  function correctnessLabel(flashcard: Flashcard, answerIds: number[], rawAnswerData?: RawAnswerData): 'Correct' | 'Partial Credit' | 'Incorrect' {
    const max = maxScore(flashcard);
    if (max <= 0) return 'Incorrect';
    const score = nextGenScore(flashcard, answerIds, rawAnswerData);
    if (score >= max) return 'Correct';
    if (score <= 0) return 'Incorrect';
    return 'Partial Credit';
  }

  return {
    maxScore,
    nextGenScoringRule,
    nextGenScore,
    correctnessLabel,
  };
}
